// src/layouts/management/sidebar/Sidebar.tsx

import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';

import {
  Building2,
  Gamepad2,
  Home,
  Image,
  LogOut,
  Settings,
  Ticket,
  Trophy,
  UserCheck,
  Users,
} from 'lucide-react';

import {
  ProfileEditModal,
} from '../../../features/member/components/ProfileEditModal';

import RouteConfig from '../../../routes/RouteConfig';

import type {
  RouteItem,
} from '../../../routes/RouteConfig';

import type {
  SidebarProps,
} from './sidebar.types';

import {
  useManagementSidebar,
} from './hooks/useManagementSidebar';


const Sidebar:
  React.FC<SidebarProps> = ({
    isOpen,
    currentPath,
    onNavigate,
  }) => {

    const {
      user,
      storeBalances,
      storeNameMap,
      isAdmin,
      isSuperAdmin,
      isLoggingOut,
      getMenuClassName,
      loadStoreBalances,
      logout,
    } =
      useManagementSidebar({
        currentPath,
        onNavigate,
      });


    /**
     * =========================================================
     * 권한별 홈
     * =========================================================
     */
    const homeRoute =
      useMemo<
        RouteItem | null
      >(
        () => {

          if (
            isAdmin
          ) {
            return (
              RouteConfig
                .Management
                .AdminHome
            );
          }


          if (
            isSuperAdmin
          ) {
            return (
              RouteConfig
                .SuperAdmin
                .Home
            );
          }


          return null;
        },
        [
          isAdmin,
          isSuperAdmin,
        ],
      );


    /**
     * =========================================================
     * 프로필 모달
     * =========================================================
     */
    const [
      isProfileModalOpen,
      setIsProfileModalOpen,
    ] =
      useState(
        false,
      );


    /**
     * =========================================================
     * 프로필 수정 완료
     * =========================================================
     */
    const handleProfileUpdated =
      useCallback(
        async (): Promise<void> => {

          setIsProfileModalOpen(
            false,
          );


          await loadStoreBalances();

        },
        [
          loadStoreBalances,
        ],
      );


    return (
      <>

        <aside
          className={[
            /**
             * 핵심:
             *
             * h-full
             * min-h-0
             * flex-col
             *
             * 하위 스크롤 영역이
             * 제대로 높이를 계산할 수 있도록 유지
             */
            'flex h-full min-h-0 flex-col',

            /**
             * aside 자체는 넘치지 않게
             */
            'overflow-hidden',

            'bg-gray-900 text-white',

            'transition-all duration-300',

            isOpen
              ? 'w-full'
              : 'w-0',
          ].join(
            ' ',
          )}
        >

          {/* =================================================
              Logo
              ================================================= */}

          <div
            className="
              shrink-0
              px-6
              pb-3
              pt-5
              sm:pt-6
            "
          >

            <div
              className="
                px-4
              "
            >
              <img
                src="/login.png"
                alt="La Poit Logo"
                className="
                  h-12
                  w-auto
                  scale-150
                  sm:h-14
                "
              />
            </div>

          </div>


          {/* =================================================
              메뉴 스크롤 영역
              
              핵심 수정:
              flex-1
              min-h-0
              overflow-y-auto
              ================================================= */}

          <div
            className="
              min-h-0
              flex-1
              overflow-y-auto
              overscroll-contain
              px-6
              pb-4

              [scrollbar-width:thin]
            "
          >

            <nav
              className="
                space-y-2
              "
            >

              {/* =================================================
                  Home
                  ================================================= */}

              {homeRoute && (

                <button
                  type="button"
                  onClick={() =>
                    onNavigate(
                      homeRoute,
                    )
                  }
                  className={
                    getMenuClassName(
                      homeRoute.fullPath,
                    )
                  }
                >

                  <Home
                    size={20}
                    strokeWidth={1.9}
                  />

                  <span>
                    홈
                  </span>

                </button>

              )}


              {/* =================================================
                  Games
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  onNavigate(
                    RouteConfig
                      .Management
                      .Games,
                  )
                }
                className={
                  getMenuClassName(
                    RouteConfig
                      .Management
                      .Games
                      .fullPath,
                  )
                }
              >

                <Gamepad2
                  size={20}
                  strokeWidth={1.9}
                />

                <span>
                  게임
                </span>

              </button>


              {/* =================================================
                  Members
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  onNavigate(
                    RouteConfig
                      .Management
                      .Members,
                  )
                }
                className={
                  getMenuClassName(
                    RouteConfig
                      .Management
                      .Members
                      .fullPath,
                  )
                }
              >

                <Users
                  size={20}
                  strokeWidth={1.9}
                />

                <span>
                  회원
                </span>

              </button>


              {/* =================================================
                  Waiting Members
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  onNavigate(
                    RouteConfig
                      .Management
                      .WaitingMembers,
                  )
                }
                className={
                  getMenuClassName(
                    RouteConfig
                      .Management
                      .WaitingMembers
                      .fullPath,
                  )
                }
              >

                <UserCheck
                  size={20}
                  strokeWidth={1.9}
                />

                <span>
                  대기중인 회원
                </span>

              </button>


              {/* =================================================
                  Coupons
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  onNavigate(
                    RouteConfig
                      .Management
                      .Coupons,
                  )
                }
                className={
                  getMenuClassName(
                    RouteConfig
                      .Management
                      .Coupons
                      .fullPath,
                  )
                }
              >

                <Ticket
                  size={20}
                  strokeWidth={1.9}
                />

                <span>
                  쿠폰 관리
                </span>

              </button>


              {/* =================================================
                  ADMIN ONLY
                  ================================================= */}

              {isAdmin && (
                <>

                  <div
                    className="
                      my-3
                      border-t
                      border-gray-700
                    "
                  />


                  <button
                    type="button"
                    onClick={() =>
                      onNavigate(
                        RouteConfig
                          .Management
                          .Setting,
                      )
                    }
                    className={
                      getMenuClassName(
                        RouteConfig
                          .Management
                          .Setting
                          .fullPath,
                      )
                    }
                  >

                    <Settings
                      size={20}
                      strokeWidth={1.9}
                    />

                    <span>
                      매장 관리
                    </span>

                  </button>

                </>
              )}


              {/* =================================================
                  SUPERADMIN ONLY
                  ================================================= */}

              {isSuperAdmin && (
                <>

                  <div
                    className="
                      my-3
                      border-t
                      border-gray-700
                    "
                  />


                  {/* 지점 관리 */}
                  <button
                    type="button"
                    onClick={() =>
                      onNavigate(
                        RouteConfig
                          .SuperAdmin
                          .Stores,
                      )
                    }
                    className={
                      getMenuClassName(
                        RouteConfig
                          .SuperAdmin
                          .Stores
                          .fullPath,
                      )
                    }
                  >

                    <Building2
                      size={20}
                      strokeWidth={1.9}
                    />

                    <span>
                      지점 관리
                    </span>

                  </button>


                  {/* 대회 관리 */}
                  <button
                    type="button"
                    onClick={() =>
                      onNavigate(
                        RouteConfig
                          .SuperAdmin
                          .Tournaments,
                      )
                    }
                    className={
                      getMenuClassName(
                        RouteConfig
                          .SuperAdmin
                          .Tournaments
                          .fullPath,
                      )
                    }
                  >

                    <Trophy
                      size={20}
                      strokeWidth={1.9}
                    />

                    <span>
                      대회 관리
                    </span>

                  </button>


                  {/* 포스터 관리 */}
                  <button
                    type="button"
                    onClick={() =>
                      onNavigate(
                        RouteConfig
                          .SuperAdmin
                          .Posters,
                      )
                    }
                    className={
                      getMenuClassName(
                        RouteConfig
                          .SuperAdmin
                          .Posters
                          .fullPath,
                      )
                    }
                  >

                    <Image
                      size={20}
                      strokeWidth={1.9}
                    />

                    <span>
                      포스터 관리
                    </span>

                  </button>


                  {/* 시스템 관리 */}
                  <button
                    type="button"
                    onClick={() =>
                      onNavigate(
                        RouteConfig
                          .SuperAdmin
                          .Admins,
                      )
                    }
                    className={
                      getMenuClassName(
                        RouteConfig
                          .SuperAdmin
                          .Admins
                          .fullPath,
                      )
                    }
                  >

                    <Settings
                      size={20}
                      strokeWidth={1.9}
                    />

                    <span>
                      시스템 관리
                    </span>

                  </button>

                </>
              )}

            </nav>

          </div>


          {/* =================================================
              하단 사용자 영역
              
              shrink-0:
              메뉴가 아무리 길어져도
              로그아웃 영역이 밀려나지 않음
              ================================================= */}

          <div
            className="
              shrink-0
              border-t
              border-gray-800
              bg-gray-900
              p-4

              pb-[calc(1rem+env(safe-area-inset-bottom))]
            "
          >

            {/* =================================================
                포인트
                ================================================= */}

            {user && (

              <div
                className="
                  mb-3
                  max-h-28
                  space-y-1
                  overflow-y-auto
                  text-xs
                "
              >

                {/* 공용 시드 */}

                <div
                  className="
                    flex
                    items-center
                    justify-between
                    gap-3
                    text-gray-300
                  "
                >

                  <span
                    className="
                      text-gray-400
                    "
                  >
                    시드 잔액
                  </span>


                  <span
                    className="
                      font-semibold
                      text-white
                    "
                  >
                    {(
                      user.point ??
                      0
                    ).toLocaleString(
                      'ko-KR',
                    )}
                    P
                  </span>

                </div>


                {/* 매장 포인트 */}

                {storeBalances
                  .filter(
                    (
                      balance,
                    ) =>
                      balance.point !==
                      0,
                  )
                  .map(
                    (
                      balance,
                    ) => {

                      const storeName =
                        storeNameMap.get(
                          balance.storeId,
                        ) ??
                        balance.storeName ??
                        `지점 ${balance.storeId}`;


                      return (
                        <div
                          key={
                            balance.storeId
                          }
                          className="
                            flex
                            items-center
                            justify-between
                            gap-3
                            text-gray-300
                          "
                        >

                          <span
                            className="
                              min-w-0
                              max-w-[150px]
                              truncate
                              text-gray-400
                            "
                          >
                            {storeName}
                          </span>


                          <span
                            className="
                              shrink-0
                              font-medium
                            "
                          >
                            {(
                              balance.point ??
                              0
                            ).toLocaleString(
                              'ko-KR',
                            )}
                            P
                          </span>

                        </div>
                      );
                    },
                  )}


                <div
                  className="
                    mt-2
                    border-t
                    border-gray-700
                  "
                />

              </div>

            )}


            {/* =================================================
                프로필
                ================================================= */}

            <button
              type="button"
              onClick={() =>
                setIsProfileModalOpen(
                  true,
                )
              }
              className="
                -m-2
                flex
                w-[calc(100%+16px)]
                items-center
                gap-3
                rounded-lg
                p-2
                text-left
                transition
                hover:bg-gray-800
              "
            >

              <div
                className="
                  flex
                  h-10
                  w-10
                  shrink-0
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-full
                  bg-gray-600
                "
              >

                {user?.profileImageUrl ? (

                  <img
                    src={
                      user.profileImageUrl
                    }
                    alt="프로필"
                    className="
                      h-full
                      w-full
                      object-cover
                    "
                  />

                ) : (

                  <Users
                    size={20}
                    strokeWidth={1.9}
                  />

                )}

              </div>


              <div
                className="
                  min-w-0
                  flex-1
                "
              >

                <p
                  className="
                    truncate
                    text-sm
                    font-semibold
                    text-white
                  "
                >
                  {user?.userNickname ||
                    user?.userName ||
                    'Admin User'}
                </p>


                <p
                  className="
                    mt-0.5
                    truncate
                    text-xs
                    text-gray-400
                  "
                >
                  {user?.userId ?? ''}
                </p>

              </div>

            </button>


            {/* =================================================
                로그아웃
                ================================================= */}

            <button
              type="button"
              onClick={() =>
                void logout()
              }
              disabled={
                isLoggingOut
              }
              className="
                mt-3
                flex
                w-full
                items-center
                justify-center
                gap-2
                rounded-lg
                border
                border-gray-700
                px-3
                py-2.5
                text-xs
                font-semibold
                text-gray-300
                transition
                hover:border-red-700
                hover:bg-red-950/40
                hover:text-red-300
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >

              <LogOut
                size={16}
                strokeWidth={2}
              />


              {isLoggingOut
                ? '로그아웃 중...'
                : '로그아웃'}

            </button>

          </div>

        </aside>


        {/* =================================================
            프로필 수정 모달
            ================================================= */}

        <ProfileEditModal
          open={
            isProfileModalOpen
          }
          onClose={() =>
            setIsProfileModalOpen(
              false,
            )
          }
          onUpdated={
            handleProfileUpdated
          }
        />

      </>
    );
  };


export default Sidebar;